/* @flow */

import * as React from 'react'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'

import WebshopActionButton from './WebshopActionButton'
import CartIconFilled from '../../../assets/images/icons/lnr-cart-filled.svg'
import CartIcon from '../../../assets/images/icons/lnr-cart.svg'
import WebshopProductTable from './WebshopProductTable'
import { useProductTableControls } from '../../products/components/ProductTable2'
import EmployeeDiscountModalButton from './EmployeeDiscountModalButton'

import { useCartActions } from '../hooks'

import { Tooltip, SessionContext } from '../../shared'
import type {
  CartRow,
  ConnectedBrand,
  Product,
  WebshopSession,
} from '../../types'

import AddToCartV2QuickBuy from './AddToCartV2QuickBuy'

type Props = {
  brand: ConnectedBrand,
  cartIsSaving: boolean,
  cartRows: Array<CartRow>,
  onCartUpdate: Function,
  product: Product,
  refreshCartRows: Function,
  webshopSession: WebshopSession,
}

const AddToCartV2 = ({
  brand,
  cartIsSaving,
  cartRows,
  onCartUpdate,
  product,
  refreshCartRows,
  webshopSession,
}: Props) => {
  const { user } = React.useContext(SessionContext)
  const [showQuantityModal, setShowQuantityModal] = React.useState(false)
  const useDrops = brand.settings.webshop_settings.use_drops

  const cartRowsOfProduct = React.useMemo(() => {
    return cartRows.filter(row => row.product_id === product.id)
  }, [cartRows, product])

  const { disableBuy, firstVariant, noPrice, quickBuy } = React.useMemo(() => {
    const someVariantOrProductWithColli =
      product.colli.length > 0 ||
      product.variants.some(v => Array.isArray(v.colli) && v.colli.length > 0)
    const noPrice = product.price === null ? true : false

    const firstVariant = product.variants[0]

    const disableBuy = noPrice
    const quickBuy =
      !useDrops &&
      product.quick_buy &&
      !someVariantOrProductWithColli &&
      !product.product_is_unique

    return {
      disableBuy,
      firstVariant,
      noPrice,
      quickBuy,
    }
  }, [product, useDrops])

  const { inCart, totalQuantityInCart } = React.useMemo(() => {
    const variantIds = product.variants.map(variant => variant.id)
    const rowsWithQuantity = cartRowsOfProduct.filter(
      row => variantIds.includes(row.variant_id) && row.quantity > 0
    )

    const inCart = rowsWithQuantity.length > 0
    const totalQuantityInCart = rowsWithQuantity.reduce(
      (carry, row) => (carry += row.quantity),
      0
    )

    return {
      inCart,
      totalQuantityInCart,
    }
  }, [cartRowsOfProduct, product])

  const changeOneVariantQuantity = React.useCallback(
    (quantity: number) => {
      const productId = product.id
      const variantId = firstVariant.id

      const newItem = {
        variant_id: variantId,
        drop_id: null,
        quantity: 0,
      }

      let existingItem =
        cartRows.find(
          ({ variant_id, product_id }) =>
            variant_id === variantId && product_id === productId
        ) || newItem

      const updates = [
        {
          ...existingItem,
          quantity,
        },
      ]

      return onCartUpdate(updates).then(response => {
        if (!response.error) {
          return refreshCartRows()
        }
      })
    },
    [brand, firstVariant, cartRows, onCartUpdate, refreshCartRows]
  )

  const onSubmitQuickBuy = React.useCallback(
    (...args) => {
      return onCartUpdate(...args).then(response => {
        if (!response.error) {
          setShowQuantityModal(false)

          refreshCartRows()
        }
      })
    },
    [onCartUpdate, refreshCartRows, setShowQuantityModal]
  )

  let toggleFunction
  const isFixedDrop = webshopSession.drop_id !== null
  const quickAddBecauseProductIsUnique =
    (!useDrops || isFixedDrop) &&
    product.product_is_unique &&
    product.variants.length == 1
  const useQuickAddBecauseFixedDrop =
    user.quick_cart_add_quantities === false && isFixedDrop

  // when the product is unique and we dont buy drops we might as well just add it
  // to the cart
  if (quickAddBecauseProductIsUnique || useQuickAddBecauseFixedDrop) {
    toggleFunction = () => {
      let dropId = null
      if (webshopSession.drop_id) {
        dropId = webshopSession.drop_id
      }

      const updateRows = []
      for (let variant of product.variants) {
        if (inCart) {
          const cartRowOfVariant = cartRowsOfProduct.find(
            c => c.variant_id == variant.id
          )

          updateRows.push({
            variant_id: variant.id,
            drop_id: dropId,
            delete: true,
            quantity: 0,
          })
        } else {
          updateRows.push({
            variant_id: variant.id,
            drop_id: dropId,
            quantity: 1,
          })
        }
      }

      return onCartUpdate(updateRows).then(response => {
        if (!response.error) {
          return refreshCartRows()
        }
      })
    }

    // either the product has variations or
  } else {
    toggleFunction = () => setShowQuantityModal(true)
  }

  return (
    <Container>
      {showQuantityModal && (
        <CartQuickBuyModal
          brand={brand}
          onHide={() => setShowQuantityModal(false)}
          onUpdate={onSubmitQuickBuy}
          product={product}
          showModal={showQuantityModal}
          webshopSession={webshopSession}
        />
      )}

      {quickBuy && firstVariant && (
        <AddToCartV2QuickBuy
          noPrice={noPrice}
          onCartChange={changeOneVariantQuantity}
          totalQuantityInCart={totalQuantityInCart}
          cartIsSaving={cartIsSaving}
        />
      )}
      {!quickBuy && (
        <Tooltip id={`webshop-${product.id}-add-to-cart`} tip="Add to cart">
          <div
            className={`webshop-search-product-actions-buy ${
              inCart ? 'no-pointer' : ''
            } ${disableBuy ? 'no-pointer disable' : ''} `}
            onClick={toggleFunction}
          >
            <div className="center-wrapper">
              {cartIsSaving && (
                <span className="fa fa-spinner fa-spin splash-spinner" />
              )}
              {!cartIsSaving && (
                <img src={!inCart ? CartIcon : CartIconFilled} />
              )}
            </div>
          </div>
        </Tooltip>
      )}
    </Container>
  )
}

export default AddToCartV2

const Container = styled.div`
  flex: 3;
`

// We have to make this component into a separate component because we need to fetch cart lines when it is shown.
// The reason why we need to fetch cart lines is that the cart lines indicate which drops are available. And we cannot fetch
// this for all products in the search result, so we fetch it on the individual product when opening the modal
const CartQuickBuyModal = ({
  brand,
  onHide,
  onUpdate,
  product,
  showModal,
  webshopSession,
  ...rest
}) => {
  const [productTableControls, onProductTableControlsChange] =
    useProductTableControls()

  const {
    cartIsDirty,
    cartView,
    updatedLines: updatedCartLines,
    onLineChange,
    onSaveEmployeeDiscountLines,
  } = useCartActions(
    brand.id,
    webshopSession.id,
    true,
    false,
    false,
    product.id,
    product.split_key
  )

  return (
    <Modal
      bsSize="large"
      show={showModal}
      onHide={onHide}
      dialogClassName="xl-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>How many do you want to put in the cart?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <EmployeeDiscountModalButton
          brandId={brand.id}
          customer={webshopSession.customer}
          deliveryStatus={cartView.deliveryStatus}
          filterVariantsWithoutLinesByDefault
          lines={updatedCartLines}
          onLineChange={onLineChange}
          onSaveEmployeeDiscountLines={onSaveEmployeeDiscountLines}
          onTableControlsChange={onProductTableControlsChange}
          prices={cartView.prices}
          products={cartView.productTableProducts}
          tableControls={productTableControls}
          tableId="show_product"
          webshopSession={webshopSession}
          render={({ onClick }) => (
            <EmployeeDiscountContainer>
              <WebshopActionButton color="white" size="large" onClick={onClick}>
                Employee discount
              </WebshopActionButton>
            </EmployeeDiscountContainer>
          )}
        />

        <WebshopProductTable
          brandId={brand.id}
          customer={webshopSession.customer}
          deliveryStatus={cartView.deliveryStatus}
          filterVariantsWithoutLinesByDefault
          lines={updatedCartLines}
          onLineChange={onLineChange}
          onTableControlsChange={onProductTableControlsChange}
          prices={cartView.prices}
          products={cartView.productTableProducts}
          tableControls={productTableControls}
          tableId="show_product"
          webshopSession={webshopSession}
        />
      </Modal.Body>
      <Modal.Footer>
        <WebshopActionButton size="large" color="white" onClick={onHide}>
          Cancel
        </WebshopActionButton>

        <WebshopActionButton
          color="primary"
          onClick={() => onUpdate(updatedCartLines, cartIsDirty)}
          size="large"
        >
          Update Cart
        </WebshopActionButton>
      </Modal.Footer>
    </Modal>
  )
}

const EmployeeDiscountContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`
